import type { Theme } from "@broadpaper/core";

export interface FontOption {
  value: string;
  label: string;
}

/**
 * The fonts a designer may choose, which are the fonts the theme declares.
 *
 * The pickers used to add a list of popular families — Roboto, Georgia, Times New
 * Roman — that nothing embedded. Any of them looked right on a machine that had it
 * installed and printed in a standard face, so a choice the designer offered was a
 * choice that broke the document. What `theme.fonts` declares is drawn on the canvas
 * and embedded in the file, so that is the whole list.
 *
 * A font a template already uses stays in the list, marked, even when the theme no
 * longer declares it: a native select whose value is missing shows its first option
 * instead, so the inspector would claim a font the block does not use and the next
 * unrelated change would replace it.
 */
export function fontOptions(theme: Theme, current?: string): FontOption[] {
  const first = (stack: string) => stack.split(",")[0]!.trim().replace(/^['"]|['"]$/g, "");
  const options: FontOption[] = [
    { value: "$typography.headingFont", label: `Heading font (${first(theme.typography.headingFont)})` },
    { value: "$typography.bodyFont", label: `Body font (${first(theme.typography.bodyFont)})` },
    ...(theme.typography.monoFont ? [{ value: "$typography.monoFont", label: `Mono font (${first(theme.typography.monoFont)})` }] : [])
  ];
  for (const face of theme.fonts ?? []) {
    if (!options.some((o) => o.value === face.family)) options.push({ value: face.family, label: face.family });
  }
  if (current && !options.some((o) => o.value === current)) {
    options.push({ value: current, label: `${current.startsWith("$typography.") ? current.slice("$typography.".length) : current} (not in this theme)` });
  }
  return options;
}
